import { useState, useCallback, useMemo } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'src/components/ui/card';
import { Separator } from 'src/components/ui/separator';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  CircleNotchIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  TrashIcon,
  CreditCardIcon,
  PlusIcon,
  ClockIcon,
  ArrowsClockwiseIcon,
  StarIcon,
  InfoIcon,
  BuildingIcon,
  ShieldIcon,
  UserIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { useGetIntegration } from 'src/api/rpc/hooks/integrations/useIntegrations';
import { orpc } from 'src/api/rpc/client';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  IntegrationAvailability,
  IntegrationKey,
  PaymentAccount,
  BankAccountType,
  USState,
  IndustryCode,
  CitizenshipStatus,
  CheckbookOnboardingInfo,
  PaymentsIntegration,
  // Shared display label arrays
  US_STATES,
  INDUSTRY_CODES,
  CITIZENSHIP_OPTIONS,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';
import { formatDateTime } from '@erp/shared-utils';

// Initial onboarding form state
const INITIAL_ONBOARDING_FORM: OnboardingFormData = {
  business: {
    legalBusinessName: '',
    website: '',
    taxId: '',
    businessPhone: '',
    industry: '' as IndustryCode,
    incorporationMonth: '',
    incorporationDay: '',
    incorporationYear: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '' as USState,
      zip: '',
    },
  },
  principalOfficer: {
    firstName: '',
    lastName: '',
    ssn: '',
    dobMonth: '',
    dobDay: '',
    dobYear: '',
    occupation: '',
    citizenshipStatus: '' as CitizenshipStatus,
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '' as USState,
      zip: '',
    },
  },
};

// Form data interface for onboarding (with separate date fields)
interface OnboardingFormData {
  business: {
    legalBusinessName: string;
    website: string;
    taxId: string;
    businessPhone: string;
    industry: IndustryCode | '';
    incorporationMonth: string;
    incorporationDay: string;
    incorporationYear: string;
    address: {
      line1: string;
      line2: string;
      city: string;
      state: USState | '';
      zip: string;
    };
  };
  principalOfficer: {
    firstName: string;
    lastName: string;
    ssn: string;
    dobMonth: string;
    dobDay: string;
    dobYear: string;
    occupation: string;
    citizenshipStatus: CitizenshipStatus | '';
    address: {
      line1: string;
      line2: string;
      city: string;
      state: USState | '';
      zip: string;
    };
  };
}

/**
 * Safely map Plaid account type to BankAccountType.
 * Plaid may return values like "checking", "savings", or unexpected types.
 * We normalize and validate against our allowed values.
 */
function toBankAccountType(plaidType?: string): BankAccountType {
  const typeMap: Record<string, BankAccountType> = {
    checking: 'CHECKING',
    savings: 'SAVINGS',
    business: 'BUSINESS',
  };
  return typeMap[plaidType?.toLowerCase() ?? ''] ?? 'CHECKING';
}

interface AddPaymentAccountFormData {
  name: string;
  plaidAccountId: string;
  plaidBankAccountId: string;
  routingNumber: string;
  accountNumber: string;
  accountType: BankAccountType;
  isDefault: boolean;
}

interface VerifyMicroDepositsFormData {
  amount1: string;
  amount2: string;
}

export function PaymentsIntegrationSettings({
  availability,
}: {
  availability?: IntegrationAvailability;
}) {
  const isAvailable = availability?.available ?? true;
  const unavailableReason = availability?.reason;
  const { showToast } = useToast();
  const {
    data: integration,
    isLoading,
    error,
    refetch,
  } = useGetIntegration(IntegrationKey.Payments);

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [selectedAccountForVerify, setSelectedAccountForVerify] =
    useState<PaymentAccount | null>(null);

  // Form states
  const [addForm, setAddForm] = useState<AddPaymentAccountFormData>({
    name: '',
    plaidAccountId: '',
    plaidBankAccountId: '',
    routingNumber: '',
    accountNumber: '',
    accountType: 'CHECKING',
    isDefault: false,
  });
  const [verifyForm, setVerifyForm] = useState<VerifyMicroDepositsFormData>({
    amount1: '',
    amount2: '',
  });
  const [onboardingForm, setOnboardingForm] = useState<OnboardingFormData>(
    INITIAL_ONBOARDING_FORM,
  );

  // Check if this is first-time setup (no existing Checkbook user)
  const requiresOnboarding = useMemo(() => {
    if (!integration) return true;
    const paymentsIntegration = integration as PaymentsIntegration;
    return !paymentsIntegration.settings?.checkbookUser;
  }, [integration]);

  // Mutations
  const addPaymentAccount = useMutation(
    orpc.integrations.payments.addPaymentAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const verifyMicroDeposits = useMutation(
    orpc.integrations.payments.verifyMicroDeposits.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const resendMicroDeposits = useMutation(
    orpc.integrations.payments.resendMicroDeposits.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const deletePaymentAccount = useMutation(
    orpc.integrations.payments.deletePaymentAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const setDefaultPaymentAccount = useMutation(
    orpc.integrations.payments.setDefaultPaymentAccount.mutationOptions({
      onSuccess: () => refetch(),
    }),
  );
  const updateBusinessInfo = useMutation(
    orpc.integrations.payments.updateBusinessInfo.mutationOptions(),
  );

  // Edit business info dialog state
  const [isEditBusinessDialogOpen, setIsEditBusinessDialogOpen] =
    useState(false);
  const [editBusinessForm, setEditBusinessForm] = useState<OnboardingFormData>(
    INITIAL_ONBOARDING_FORM,
  );

  // Query for available Plaid accounts
  const plaidAccountsQuery = useQuery(
    orpc.integrations.payments.getAvailablePlaidAccounts.queryOptions(),
  );

  const availablePlaidAccounts = plaidAccountsQuery.data;
  // Show loading state when query hasn't resolved yet (no data and not errored)
  const isPlaidAccountsLoading =
    !plaidAccountsQuery.data && !plaidAccountsQuery.error;

  // Get unlinked Plaid accounts
  const unlinkedPlaidAccounts =
    availablePlaidAccounts?.filter((acc) => !acc.isAlreadyLinked) ?? [];

  // Validate onboarding form
  const validateOnboardingForm = (): string | null => {
    const { business, principalOfficer } = onboardingForm;

    // Business validation
    if (!business.legalBusinessName.trim()) {
      return 'Legal business name is required';
    }
    if (!business.taxId.trim() || !/^\d{2}-?\d{7}$/.test(business.taxId)) {
      return 'Tax ID must be in format XX-XXXXXXX';
    }
    if (!business.industry) {
      return 'Industry is required';
    }
    if (
      !business.incorporationMonth ||
      !business.incorporationDay ||
      !business.incorporationYear
    ) {
      return 'Business incorporation date is required';
    }
    if (!business.address.line1.trim()) {
      return 'Business street address is required';
    }
    if (!business.address.city.trim()) {
      return 'Business city is required';
    }
    if (!business.address.state) {
      return 'Business state is required';
    }
    if (
      !business.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(business.address.zip)
    ) {
      return 'Business ZIP code must be in format 12345 or 12345-6789';
    }

    // Principal officer validation
    if (!principalOfficer.firstName.trim()) {
      return 'Principal officer first name is required';
    }
    if (!principalOfficer.lastName.trim()) {
      return 'Principal officer last name is required';
    }
    if (
      !principalOfficer.ssn.trim() ||
      !/^\d{3}-?\d{2}-?\d{4}$/.test(principalOfficer.ssn)
    ) {
      return 'SSN must be 9 digits';
    }
    if (
      !principalOfficer.dobMonth ||
      !principalOfficer.dobDay ||
      !principalOfficer.dobYear
    ) {
      return 'Principal officer date of birth is required';
    }
    if (!principalOfficer.occupation.trim()) {
      return 'Principal officer occupation is required';
    }
    if (!principalOfficer.citizenshipStatus) {
      return 'Principal officer citizenship status is required';
    }
    if (!principalOfficer.address.line1.trim()) {
      return 'Principal officer street address is required';
    }
    if (!principalOfficer.address.city.trim()) {
      return 'Principal officer city is required';
    }
    if (!principalOfficer.address.state) {
      return 'Principal officer state is required';
    }
    if (
      !principalOfficer.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(principalOfficer.address.zip)
    ) {
      return 'Principal officer ZIP code must be in format 12345 or 12345-6789';
    }

    return null;
  };

  // Build onboarding info for API
  const buildOnboardingInfo = (): CheckbookOnboardingInfo => {
    const { business, principalOfficer } = onboardingForm;
    return {
      business: {
        legalBusinessName: business.legalBusinessName,
        website: business.website || undefined,
        taxId: business.taxId,
        businessPhone: business.businessPhone || undefined,
        industry: business.industry as IndustryCode,
        incorporationDate: `${business.incorporationMonth}/${business.incorporationDay}/${business.incorporationYear}`,
        address: {
          line1: business.address.line1,
          line2: business.address.line2 || undefined,
          city: business.address.city,
          state: business.address.state as USState,
          zip: business.address.zip,
        },
      },
      principalOfficer: {
        firstName: principalOfficer.firstName,
        lastName: principalOfficer.lastName,
        ssn: principalOfficer.ssn,
        dateOfBirth: `${principalOfficer.dobMonth}/${principalOfficer.dobDay}/${principalOfficer.dobYear}`,
        occupation: principalOfficer.occupation,
        citizenshipStatus:
          principalOfficer.citizenshipStatus as CitizenshipStatus,
        address: {
          line1: principalOfficer.address.line1,
          line2: principalOfficer.address.line2 || undefined,
          city: principalOfficer.address.city,
          state: principalOfficer.address.state as USState,
          zip: principalOfficer.address.zip,
        },
      },
    };
  };

  // Handle add payment account
  const handleAddAccount = async () => {
    if (!addForm.name.trim()) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter an account name',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (!addForm.plaidAccountId || !addForm.plaidBankAccountId) {
      showToast({
        title: 'Validation Error',
        message: 'Please select a bank account',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (addForm.routingNumber.length !== 9) {
      showToast({
        title: 'Validation Error',
        message: 'Routing number must be 9 digits',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    if (addForm.accountNumber.length < 4) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter a valid account number',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    // Validate onboarding info if this is first-time setup
    if (requiresOnboarding) {
      const onboardingError = validateOnboardingForm();
      if (onboardingError) {
        showToast({
          title: 'Validation Error',
          message: onboardingError,
          color: 'error',
          icon: 'error',
        });
        return;
      }
    }

    try {
      await addPaymentAccount.mutateAsync({
        name: addForm.name,
        plaidAccountId: addForm.plaidAccountId,
        plaidBankAccountId: addForm.plaidBankAccountId,
        routingNumber: addForm.routingNumber,
        accountNumber: addForm.accountNumber,
        accountType: addForm.accountType,
        isDefault: addForm.isDefault,
        // Include onboarding info only for first-time setup
        onboardingInfo: requiresOnboarding ? buildOnboardingInfo() : undefined,
      });

      showToast({
        title: 'Payment Account Added',
        message:
          'Two small deposits will be sent to your account for verification. This typically takes 1-2 business days.',
        color: 'success',
        icon: 'check',
      });

      setIsAddDialogOpen(false);
      setAddForm({
        name: '',
        plaidAccountId: '',
        plaidBankAccountId: '',
        routingNumber: '',
        accountNumber: '',
        accountType: 'CHECKING',
        isDefault: false,
      });
      setOnboardingForm(INITIAL_ONBOARDING_FORM);
    } catch (error) {
      showToast({
        title: 'Failed to Add Account',
        message:
          error instanceof Error
            ? error.message
            : 'Please verify your details and try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle verify micro-deposits
  const handleVerifyMicroDeposits = async () => {
    if (!selectedAccountForVerify) return;

    const amount1 = parseFloat(verifyForm.amount1);
    const amount2 = parseFloat(verifyForm.amount2);

    if (isNaN(amount1) || isNaN(amount2) || amount1 <= 0 || amount2 <= 0) {
      showToast({
        title: 'Validation Error',
        message: 'Please enter both deposit amounts',
        color: 'error',
        icon: 'error',
      });
      return;
    }

    try {
      await verifyMicroDeposits.mutateAsync({
        paymentAccountId: selectedAccountForVerify.id,
        amount1,
        amount2,
      });

      showToast({
        title: 'Account Verified',
        message: 'Your payment account is now ready to use!',
        color: 'success',
        icon: 'check',
      });

      setIsVerifyDialogOpen(false);
      setSelectedAccountForVerify(null);
      setVerifyForm({ amount1: '', amount2: '' });
    } catch (error) {
      showToast({
        title: 'Verification Failed',
        message: 'The amounts entered do not match. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle resend micro-deposits
  const handleResendMicroDeposits = async (accountId: string) => {
    try {
      await resendMicroDeposits.mutateAsync({ paymentAccountId: accountId });

      showToast({
        title: 'Micro-Deposits Resent',
        message:
          'New deposits have been initiated. Please allow 1-2 business days.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Failed to Resend',
        message: 'Please try again later.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle delete account
  const handleDeleteAccount = async (accountId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to remove this payment account? This action cannot be undone.',
      )
    ) {
      return;
    }

    try {
      await deletePaymentAccount.mutateAsync({ paymentAccountId: accountId });

      showToast({
        title: 'Account Removed',
        message: 'Payment account has been successfully removed.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Removal Failed',
        message: 'Failed to remove payment account. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle set default
  const handleSetDefault = async (accountId: string) => {
    try {
      await setDefaultPaymentAccount.mutateAsync({
        paymentAccountId: accountId,
      });

      showToast({
        title: 'Default Account Updated',
        message: 'This account will now be used for payments by default.',
        color: 'success',
        icon: 'check',
      });
    } catch (error) {
      showToast({
        title: 'Update Failed',
        message: 'Failed to set default account. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Validate edit business form (SSN and DOB are optional for updates)
  const validateEditBusinessForm = (): string | null => {
    const { business, principalOfficer } = editBusinessForm;

    // Business validation
    if (!business.legalBusinessName.trim()) {
      return 'Legal business name is required';
    }
    if (!business.taxId.trim() || !/^\d{2}-?\d{7}$/.test(business.taxId)) {
      return 'Tax ID must be in format XX-XXXXXXX';
    }
    if (!business.industry) {
      return 'Industry is required';
    }
    if (
      !business.incorporationMonth ||
      !business.incorporationDay ||
      !business.incorporationYear
    ) {
      return 'Business incorporation date is required';
    }
    if (!business.address.line1.trim()) {
      return 'Business street address is required';
    }
    if (!business.address.city.trim()) {
      return 'Business city is required';
    }
    if (!business.address.state) {
      return 'Business state is required';
    }
    if (
      !business.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(business.address.zip)
    ) {
      return 'Business ZIP code must be in format 12345 or 12345-6789';
    }

    // Principal officer validation (SSN and DOB optional for updates)
    if (!principalOfficer.firstName.trim()) {
      return 'Principal officer first name is required';
    }
    if (!principalOfficer.lastName.trim()) {
      return 'Principal officer last name is required';
    }
    // SSN is optional for updates, but if provided must be valid
    if (
      principalOfficer.ssn.trim() &&
      !/^\d{3}-?\d{2}-?\d{4}$/.test(principalOfficer.ssn)
    ) {
      return 'SSN must be 9 digits if provided';
    }
    if (!principalOfficer.occupation.trim()) {
      return 'Principal officer occupation is required';
    }
    if (!principalOfficer.citizenshipStatus) {
      return 'Principal officer citizenship status is required';
    }
    if (!principalOfficer.address.line1.trim()) {
      return 'Principal officer street address is required';
    }
    if (!principalOfficer.address.city.trim()) {
      return 'Principal officer city is required';
    }
    if (!principalOfficer.address.state) {
      return 'Principal officer state is required';
    }
    if (
      !principalOfficer.address.zip.trim() ||
      !/^\d{5}(-\d{4})?$/.test(principalOfficer.address.zip)
    ) {
      return 'Principal officer ZIP code must be in format 12345 or 12345-6789';
    }

    return null;
  };

  // Handle update business info
  const handleUpdateBusinessInfo = async () => {
    const error = validateEditBusinessForm();
    if (error) {
      showToast({
        title: 'Validation Error',
        message: error,
        color: 'error',
        icon: 'error',
      });
      return;
    }

    const { business, principalOfficer } = editBusinessForm;

    try {
      await updateBusinessInfo.mutateAsync({
        business: {
          legalBusinessName: business.legalBusinessName,
          website: business.website || '',
          taxId: business.taxId,
          businessPhone: business.businessPhone || undefined,
          industry: business.industry as IndustryCode,
          incorporationDate: `${business.incorporationMonth}/${business.incorporationDay}/${business.incorporationYear}`,
          address: {
            line1: business.address.line1,
            line2: business.address.line2 || undefined,
            city: business.address.city,
            state: business.address.state as USState,
            zip: business.address.zip,
          },
        },
        principalOfficer: {
          firstName: principalOfficer.firstName,
          lastName: principalOfficer.lastName,
          // Only include SSN if provided (optional for updates)
          ssn: principalOfficer.ssn.trim() || undefined,
          // Only include DOB if all parts provided
          dateOfBirth:
            principalOfficer.dobMonth &&
            principalOfficer.dobDay &&
            principalOfficer.dobYear
              ? `${principalOfficer.dobMonth}/${principalOfficer.dobDay}/${principalOfficer.dobYear}`
              : undefined,
          occupation: principalOfficer.occupation,
          citizenshipStatus:
            principalOfficer.citizenshipStatus as CitizenshipStatus,
          address: {
            line1: principalOfficer.address.line1,
            line2: principalOfficer.address.line2 || undefined,
            city: principalOfficer.address.city,
            state: principalOfficer.address.state as USState,
            zip: principalOfficer.address.zip,
          },
        },
      });

      showToast({
        title: 'Business Information Updated',
        message: 'Your business information has been successfully updated.',
        color: 'success',
        icon: 'check',
      });

      setIsEditBusinessDialogOpen(false);
    } catch (error) {
      showToast({
        title: 'Update Failed',
        message:
          error instanceof Error
            ? error.message
            : 'Failed to update business information. Please try again.',
        color: 'error',
        icon: 'error',
      });
    }
  };

  // Handle Plaid account selection in dialog
  const handlePlaidAccountChange = useCallback(
    (value: string) => {
      const [plaidAccountId, plaidBankAccountId] = value.split(':');
      const selectedAccount = availablePlaidAccounts?.find(
        (acc) =>
          acc.plaidAccountId === plaidAccountId &&
          acc.plaidBankAccountId === plaidBankAccountId,
      );

      setAddForm((prev) => ({
        ...prev,
        plaidAccountId,
        plaidBankAccountId,
        name:
          prev.name ||
          `${selectedAccount?.institutionName} ${selectedAccount?.accountType || 'Account'}`,
      }));
    },
    [availablePlaidAccounts],
  );

  // Handle opening add dialog with a specific Plaid account pre-selected
  const handleOpenAddDialog = useCallback(
    (plaidAccount?: {
      plaidAccountId: string;
      plaidBankAccountId: string;
      institutionName: string;
      accountName?: string;
      accountMask?: string;
      accountType?: string;
    }) => {
      if (plaidAccount) {
        setAddForm({
          name: `${plaidAccount.institutionName} ${plaidAccount.accountType || 'Account'}`,
          plaidAccountId: plaidAccount.plaidAccountId,
          plaidBankAccountId: plaidAccount.plaidBankAccountId,
          routingNumber: '',
          accountNumber: '',
          accountType: toBankAccountType(plaidAccount.accountType),
          isDefault: false,
        });
      } else {
        setAddForm({
          name: '',
          plaidAccountId: '',
          plaidBankAccountId: '',
          routingNumber: '',
          accountNumber: '',
          accountType: 'CHECKING',
          isDefault: false,
        });
      }
      setIsAddDialogOpen(true);
    },
    [],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
        <span>Loading integration settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <AlertContainer variant="destructive">
        <WarningCircleIcon className="h-4 w-4" />
        <AlertDescription>
          Failed to load integration settings. Please try refreshing the page.
        </AlertDescription>
      </AlertContainer>
    );
  }

  const accounts =
    integration?.key === IntegrationKey.Payments
      ? integration.settings.accounts
      : [];
  const hasAccounts = accounts.length > 0;

  return (
    <div className="space-y-6">
      {/* Integration Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="font-medium">Integration Active</span>
          </div>
          <Badge
            variant="outline"
            className="bg-green-100 text-green-700 border-green-300"
          >
            {hasAccounts
              ? `${accounts.length} Account${accounts.length !== 1 ? 's' : ''}`
              : 'Ready to Configure'}
          </Badge>
          {!isAvailable && (
            <Badge
              variant="outline"
              className="border-red-300 text-red-700 bg-red-50"
            >
              Unavailable
            </Badge>
          )}
        </div>
        {!isAvailable && (
          <p className="text-sm text-red-600 mt-2">
            {unavailableReason || 'Payments is not available right now.'}
          </p>
        )}
      </div>

      <Separator />

      {/* Business Information Card - Only show if Checkbook user exists */}
      {!requiresOnboarding && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BuildingIcon className="h-5 w-5" />
                  Business Information
                </CardTitle>
                <CardDescription>
                  Your business verification details on file with our payment
                  processor
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditBusinessDialogOpen(true)}
              >
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <AlertContainer>
              <InfoIcon className="h-4 w-4" />
              <AlertDescription>
                This information is stored with our payment processor
                (Checkbook) for compliance purposes. Sensitive information like
                SSN is not stored in our system.
              </AlertDescription>
            </AlertContainer>
          </CardContent>
        </Card>
      )}

      {/* Payment Accounts List */}
      {hasAccounts ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Payment Accounts</CardTitle>
            <CardDescription>
              Manage your bank accounts for vendor payments
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {accounts.map((account: PaymentAccount) => (
              <Card key={account.id} className="border-2">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div
                        className={`p-2 rounded-lg ${
                          account.checkbookStatus === 'VERIFIED'
                            ? 'bg-green-100'
                            : 'bg-yellow-100'
                        }`}
                      >
                        <CreditCardIcon
                          className={`h-5 w-5 ${
                            account.checkbookStatus === 'VERIFIED'
                              ? 'text-green-700'
                              : 'text-yellow-700'
                          }`}
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-base">
                            {account.name}
                          </h4>
                          {account.isDefault && (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200"
                            >
                              <StarIcon className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                          {account.checkbookStatus === 'VERIFIED' ? (
                            <Badge
                              variant="outline"
                              className="bg-green-50 text-green-700 border-green-200"
                            >
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="bg-yellow-50 text-yellow-700 border-yellow-200"
                            >
                              <ClockIcon className="h-3 w-3 mr-1" />
                              Pending Verification
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {account.institutionName} - {account.accountType} (
                          ****{account.accountMask})
                        </div>
                        <div className="text-sm text-gray-500">
                          Added on {formatDateTime(account.createdAt)}
                        </div>
                        {account.checkbookStatus === 'PENDING' &&
                          account.microDepositsReleasedAt && (
                            <div className="text-sm text-yellow-600">
                              Micro-deposits sent on{' '}
                              {formatDateTime(account.microDepositsReleasedAt)}.
                              Check your bank statement for two small deposits.
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {account.checkbookStatus === 'PENDING' && (
                        <>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => {
                              setSelectedAccountForVerify(account);
                              setIsVerifyDialogOpen(true);
                            }}
                          >
                            Verify
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleResendMicroDeposits(account.id)
                            }
                            disabled={resendMicroDeposits.isPending}
                            title="Resend micro-deposits"
                          >
                            {resendMicroDeposits.isPending ? (
                              <CircleNotchIcon className="h-4 w-4 animate-spin" />
                            ) : (
                              <ArrowsClockwiseIcon className="h-4 w-4" />
                            )}
                          </Button>
                        </>
                      )}
                      {account.checkbookStatus === 'VERIFIED' &&
                        !account.isDefault && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(account.id)}
                            disabled={setDefaultPaymentAccount.isPending}
                          >
                            Set Default
                          </Button>
                        )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAccount(account.id)}
                        disabled={deletePaymentAccount.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {deletePaymentAccount.isPending ? (
                          <CircleNotchIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      ) : (
        /* No Accounts - Initial Setup */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Set Up Your First Payment Account
            </CardTitle>
            <CardDescription>
              Link a bank account to start making ACH payments to vendors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                No payment accounts configured
              </h3>
              <p className="text-gray-600 mb-3">
                Select a bank account below to enable ACH payments.
              </p>
              {!isAvailable && (
                <p className="text-sm text-red-600 mb-4">
                  {unavailableReason || 'Payments is not available right now.'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plaid Accounts Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg">Available Bank Accounts</CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Plaid only provides masked account numbers for security. To
                  enable ACH payments, you'll need to enter your full routing
                  and account numbers. All banking details are encrypted and
                  stored securely.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <CardDescription>
            Bank accounts connected via Plaid that can be enabled for payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPlaidAccountsLoading ? (
            <div className="flex items-center justify-center py-8">
              <CircleNotchIcon className="h-6 w-6 animate-spin mr-2" />
              <span className="text-gray-600">
                Loading available bank accounts...
              </span>
            </div>
          ) : unlinkedPlaidAccounts.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <BuildingIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm">
                {availablePlaidAccounts && availablePlaidAccounts.length > 0
                  ? 'All connected bank accounts are already enabled for payments.'
                  : 'No bank accounts available. Please connect a bank account via Plaid first.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {unlinkedPlaidAccounts.map((account) => (
                <div
                  key={`${account.plaidAccountId}:${account.plaidBankAccountId}`}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <BuildingIcon className="h-5 w-5 text-gray-600" />
                    </div>
                    <div>
                      <div className="font-medium">
                        {account.institutionName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {account.accountName ||
                          account.accountType ||
                          'Account'}
                        {account.accountMask && ` (****${account.accountMask})`}
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleOpenAddDialog(account)}
                    disabled={!isAvailable}
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Enable
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Payment Account Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent
          className={
            requiresOnboarding
              ? 'sm:max-w-[900px] max-h-[90vh] overflow-y-auto'
              : 'sm:max-w-[500px]'
          }
        >
          <DialogHeader>
            <DialogTitle>
              {requiresOnboarding
                ? 'Set Up Payments'
                : 'Enable Payment Account'}
            </DialogTitle>
            <DialogDescription>
              {requiresOnboarding
                ? 'Complete business verification to enable ACH payments. This information is required by our payment processor for compliance.'
                : 'Enter your banking details to enable ACH payments from this account.'}
            </DialogDescription>
          </DialogHeader>

          <div
            className={
              requiresOnboarding
                ? 'grid grid-cols-1 md:grid-cols-2 gap-6 py-4'
                : 'space-y-4 py-4'
            }
          >
            {/* Left Column - Bank Account Details */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <CreditCardIcon className="h-4 w-4" />
                Bank Account Details
              </div>

              {/* Selected Bank Account Display */}
              {addForm.plaidAccountId && (
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <BuildingIcon className="h-5 w-5 text-gray-600" />
                    <div>
                      <div className="font-medium">
                        {availablePlaidAccounts?.find(
                          (acc) =>
                            acc.plaidAccountId === addForm.plaidAccountId &&
                            acc.plaidBankAccountId ===
                              addForm.plaidBankAccountId,
                        )?.institutionName || 'Bank Account'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {availablePlaidAccounts?.find(
                          (acc) =>
                            acc.plaidAccountId === addForm.plaidAccountId &&
                            acc.plaidBankAccountId ===
                              addForm.plaidBankAccountId,
                        )?.accountName ||
                          availablePlaidAccounts?.find(
                            (acc) =>
                              acc.plaidAccountId === addForm.plaidAccountId &&
                              acc.plaidBankAccountId ===
                                addForm.plaidBankAccountId,
                          )?.accountType ||
                          'Account'}
                        {(() => {
                          const mask = availablePlaidAccounts?.find(
                            (acc) =>
                              acc.plaidAccountId === addForm.plaidAccountId &&
                              acc.plaidBankAccountId ===
                                addForm.plaidBankAccountId,
                          )?.accountMask;
                          return mask ? ` (****${mask})` : '';
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Account Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Main Operating Account"
                  value={addForm.name}
                  onChange={(e) =>
                    setAddForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>

              {/* Account Type */}
              <div className="space-y-2">
                <Label htmlFor="accountType">Account Type</Label>
                <Select
                  value={addForm.accountType}
                  onValueChange={(value: BankAccountType) =>
                    setAddForm((prev) => ({ ...prev, accountType: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CHECKING">Checking</SelectItem>
                    <SelectItem value="SAVINGS">Savings</SelectItem>
                    <SelectItem value="BUSINESS">Business</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Routing Number */}
              <div className="space-y-2">
                <Label htmlFor="routingNumber">Routing Number</Label>
                <Input
                  id="routingNumber"
                  placeholder="9-digit routing number"
                  value={addForm.routingNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                    setAddForm((prev) => ({ ...prev, routingNumber: value }));
                  }}
                  maxLength={9}
                />
              </div>

              {/* Account Number */}
              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number</Label>
                <Input
                  id="accountNumber"
                  placeholder="Your full account number"
                  value={addForm.accountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setAddForm((prev) => ({ ...prev, accountNumber: value }));
                  }}
                />
                {addForm.plaidBankAccountId && (
                  <p className="text-xs text-gray-500">
                    Must match the account ending in{' '}
                    {
                      availablePlaidAccounts?.find(
                        (acc) =>
                          acc.plaidAccountId === addForm.plaidAccountId &&
                          acc.plaidBankAccountId === addForm.plaidBankAccountId,
                      )?.accountMask
                    }
                  </p>
                )}
              </div>

              {!requiresOnboarding && (
                <AlertContainer variant="default">
                  <ClockIcon className="h-4 w-4" />
                  <AlertDescription>
                    After enabling, two small deposits (under $1.00) will be
                    sent to verify your account. This typically takes 1-2
                    business days.
                  </AlertDescription>
                </AlertContainer>
              )}
            </div>

            {/* Right Column - Business & Principal Officer Info (only for first-time setup) */}
            {requiresOnboarding && (
              <div className="space-y-6">
                {/* Business Information Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <BuildingIcon className="h-4 w-4" />
                    Business Information
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="legalBusinessName">
                      Legal Business Name *
                    </Label>
                    <Input
                      id="legalBusinessName"
                      placeholder="e.g., Acme Corporation, LLC"
                      value={onboardingForm.business.legalBusinessName}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          business: {
                            ...prev.business,
                            legalBusinessName: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="taxId">Tax ID (EIN) *</Label>
                      <Input
                        id="taxId"
                        placeholder="XX-XXXXXXX"
                        value={onboardingForm.business.taxId}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^\d-]/g, '');
                          // Auto-format with dash
                          if (value.length === 2 && !value.includes('-')) {
                            value = value + '-';
                          }
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              taxId: value.slice(0, 10),
                            },
                          }));
                        }}
                        maxLength={10}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="businessPhone">Business Phone</Label>
                      <Input
                        id="businessPhone"
                        placeholder="(555) 555-5555"
                        value={onboardingForm.business.businessPhone}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              businessPhone: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        placeholder="https://example.com"
                        value={onboardingForm.business.website}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              website: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry *</Label>
                      <Select
                        value={onboardingForm.business.industry}
                        onValueChange={(value: IndustryCode) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: { ...prev.business, industry: value },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {INDUSTRY_CODES.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Incorporation Date *</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={onboardingForm.business.incorporationMonth}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              incorporationMonth: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.business.incorporationDay}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              incorporationDay: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="DD" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.business.incorporationYear}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              incorporationYear: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="YYYY" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - i;
                            return (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="businessAddress">Business Address *</Label>
                    <Input
                      id="businessAddress"
                      placeholder="Street address"
                      value={onboardingForm.business.address.line1}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          business: {
                            ...prev.business,
                            address: {
                              ...prev.business.address,
                              line1: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <Input
                      placeholder="Apt, suite, etc. (optional)"
                      value={onboardingForm.business.address.line2}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          business: {
                            ...prev.business,
                            address: {
                              ...prev.business.address,
                              line2: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="City"
                        value={onboardingForm.business.address.city}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              address: {
                                ...prev.business.address,
                                city: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <Select
                        value={onboardingForm.business.address.state}
                        onValueChange={(value: USState) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              address: {
                                ...prev.business.address,
                                state: value,
                              },
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="ZIP"
                        value={onboardingForm.business.address.zip}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d-]/g, '')
                            .slice(0, 10);
                          setOnboardingForm((prev) => ({
                            ...prev,
                            business: {
                              ...prev.business,
                              address: { ...prev.business.address, zip: value },
                            },
                          }));
                        }}
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Principal Officer Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <UserIcon className="h-4 w-4" />
                    Principal Officer Information
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-3 w-3 text-gray-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>
                          Required for regulatory compliance. This information
                          is sent directly to our payment processor and is not
                          stored in our system.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <AlertContainer>
                    <ShieldIcon className="h-4 w-4" />
                    <AlertDescription>
                      This sensitive information is transmitted directly to our
                      payment processor for identity verification and is not
                      stored in our database.
                    </AlertDescription>
                  </AlertContainer>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="principalFirstName">First Name *</Label>
                      <Input
                        id="principalFirstName"
                        placeholder="First name"
                        value={onboardingForm.principalOfficer.firstName}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              firstName: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="principalLastName">Last Name *</Label>
                      <Input
                        id="principalLastName"
                        placeholder="Last name"
                        value={onboardingForm.principalOfficer.lastName}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              lastName: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ssn">Social Security Number *</Label>
                    <Input
                      id="ssn"
                      type="password"
                      placeholder="XXX-XX-XXXX"
                      value={onboardingForm.principalOfficer.ssn}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^\d-]/g, '');
                        // Auto-format with dashes
                        if (value.length === 3 && !value.includes('-')) {
                          value = value + '-';
                        } else if (
                          value.length === 6 &&
                          value.split('-').length === 2
                        ) {
                          value = value + '-';
                        }
                        setOnboardingForm((prev) => ({
                          ...prev,
                          principalOfficer: {
                            ...prev.principalOfficer,
                            ssn: value.slice(0, 11),
                          },
                        }));
                      }}
                      maxLength={11}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Date of Birth *</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={onboardingForm.principalOfficer.dobMonth}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              dobMonth: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="MM" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.principalOfficer.dobDay}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              dobDay: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="DD" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem
                              key={i + 1}
                              value={String(i + 1).padStart(2, '0')}
                            >
                              {String(i + 1).padStart(2, '0')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={onboardingForm.principalOfficer.dobYear}
                        onValueChange={(value) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              dobYear: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="YYYY" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 80 }, (_, i) => {
                            const year = new Date().getFullYear() - 18 - i;
                            return (
                              <SelectItem key={year} value={String(year)}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="occupation">Occupation *</Label>
                      <Input
                        id="occupation"
                        placeholder="e.g., CEO, Owner"
                        value={onboardingForm.principalOfficer.occupation}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              occupation: e.target.value,
                            },
                          }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="citizenshipStatus">
                        Citizenship Status *
                      </Label>
                      <Select
                        value={
                          onboardingForm.principalOfficer.citizenshipStatus
                        }
                        onValueChange={(value: CitizenshipStatus) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              citizenshipStatus: value,
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {CITIZENSHIP_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="principalAddress">
                      Principal Officer Address *
                    </Label>
                    <Input
                      id="principalAddress"
                      placeholder="Street address"
                      value={onboardingForm.principalOfficer.address.line1}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          principalOfficer: {
                            ...prev.principalOfficer,
                            address: {
                              ...prev.principalOfficer.address,
                              line1: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <Input
                      placeholder="Apt, suite, etc. (optional)"
                      value={onboardingForm.principalOfficer.address.line2}
                      onChange={(e) =>
                        setOnboardingForm((prev) => ({
                          ...prev,
                          principalOfficer: {
                            ...prev.principalOfficer,
                            address: {
                              ...prev.principalOfficer.address,
                              line2: e.target.value,
                            },
                          },
                        }))
                      }
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Input
                        placeholder="City"
                        value={onboardingForm.principalOfficer.address.city}
                        onChange={(e) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              address: {
                                ...prev.principalOfficer.address,
                                city: e.target.value,
                              },
                            },
                          }))
                        }
                      />
                      <Select
                        value={onboardingForm.principalOfficer.address.state}
                        onValueChange={(value: USState) =>
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              address: {
                                ...prev.principalOfficer.address,
                                state: value,
                              },
                            },
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.value}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="ZIP"
                        value={onboardingForm.principalOfficer.address.zip}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d-]/g, '')
                            .slice(0, 10);
                          setOnboardingForm((prev) => ({
                            ...prev,
                            principalOfficer: {
                              ...prev.principalOfficer,
                              address: {
                                ...prev.principalOfficer.address,
                                zip: value,
                              },
                            },
                          }));
                        }}
                        maxLength={10}
                      />
                    </div>
                  </div>
                </div>

                <AlertContainer variant="default">
                  <ClockIcon className="h-4 w-4" />
                  <AlertDescription>
                    After setup, two small deposits (under $1.00) will be sent
                    to verify your account. This typically takes 1-2 business
                    days.
                  </AlertDescription>
                </AlertContainer>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={addPaymentAccount.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddAccount}
              disabled={addPaymentAccount.isPending}
            >
              {addPaymentAccount.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  {requiresOnboarding ? 'Setting up...' : 'Enabling...'}
                </>
              ) : requiresOnboarding ? (
                'Complete Setup'
              ) : (
                'Enable Account'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Micro-Deposits Dialog */}
      <Dialog open={isVerifyDialogOpen} onOpenChange={setIsVerifyDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Verify Micro-Deposits</DialogTitle>
            <DialogDescription>
              Enter the two small deposit amounts from your bank statement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="amount1">First Deposit Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <Input
                  id="amount1"
                  placeholder="0.00"
                  value={verifyForm.amount1}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d.]/g, '');
                    setVerifyForm((prev) => ({ ...prev, amount1: value }));
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount2">Second Deposit Amount</Label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <Input
                  id="amount2"
                  placeholder="0.00"
                  value={verifyForm.amount2}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d.]/g, '');
                    setVerifyForm((prev) => ({ ...prev, amount2: value }));
                  }}
                />
              </div>
            </div>

            <p className="text-sm text-gray-500">
              Look for two small deposits from "Checkbook" or similar in your
              recent transactions.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsVerifyDialogOpen(false);
                setSelectedAccountForVerify(null);
                setVerifyForm({ amount1: '', amount2: '' });
              }}
              disabled={verifyMicroDeposits.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleVerifyMicroDeposits}
              disabled={verifyMicroDeposits.isPending}
            >
              {verifyMicroDeposits.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Business Information Dialog */}
      <Dialog
        open={isEditBusinessDialogOpen}
        onOpenChange={setIsEditBusinessDialogOpen}
      >
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Business Information</DialogTitle>
            <DialogDescription>
              Update your business verification details. Sensitive information
              like SSN is optional for updates.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Business Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <BuildingIcon className="h-4 w-4" />
                Business Information
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-legalBusinessName">
                  Legal Business Name *
                </Label>
                <Input
                  id="edit-legalBusinessName"
                  placeholder="e.g., Acme Corporation, LLC"
                  value={editBusinessForm.business.legalBusinessName}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      business: {
                        ...prev.business,
                        legalBusinessName: e.target.value,
                      },
                    }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-taxId">Tax ID (EIN) *</Label>
                  <Input
                    id="edit-taxId"
                    placeholder="XX-XXXXXXX"
                    value={editBusinessForm.business.taxId}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^\d-]/g, '');
                      if (value.length === 2 && !value.includes('-')) {
                        value = value + '-';
                      }
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          taxId: value.slice(0, 10),
                        },
                      }));
                    }}
                    maxLength={10}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-businessPhone">Business Phone</Label>
                  <Input
                    id="edit-businessPhone"
                    placeholder="(555) 555-5555"
                    value={editBusinessForm.business.businessPhone}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          businessPhone: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-website">Website</Label>
                  <Input
                    id="edit-website"
                    placeholder="https://example.com"
                    value={editBusinessForm.business.website}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: { ...prev.business, website: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-industry">Industry *</Label>
                  <Select
                    value={editBusinessForm.business.industry}
                    onValueChange={(value: IndustryCode) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: { ...prev.business, industry: value },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_CODES.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Incorporation Date *</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Select
                    value={editBusinessForm.business.incorporationMonth}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          incorporationMonth: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.business.incorporationDay}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: { ...prev.business, incorporationDay: value },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="DD" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.business.incorporationYear}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          incorporationYear: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="YYYY" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 100 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-businessAddress">Business Address *</Label>
                <Input
                  id="edit-businessAddress"
                  placeholder="Street address"
                  value={editBusinessForm.business.address.line1}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      business: {
                        ...prev.business,
                        address: {
                          ...prev.business.address,
                          line1: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <Input
                  placeholder="Apt, suite, etc. (optional)"
                  value={editBusinessForm.business.address.line2}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      business: {
                        ...prev.business,
                        address: {
                          ...prev.business.address,
                          line2: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="City"
                    value={editBusinessForm.business.address.city}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          address: {
                            ...prev.business.address,
                            city: e.target.value,
                          },
                        },
                      }))
                    }
                  />
                  <Select
                    value={editBusinessForm.business.address.state}
                    onValueChange={(value: USState) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          address: { ...prev.business.address, state: value },
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="ZIP"
                    value={editBusinessForm.business.address.zip}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^\d-]/g, '')
                        .slice(0, 10);
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        business: {
                          ...prev.business,
                          address: { ...prev.business.address, zip: value },
                        },
                      }));
                    }}
                    maxLength={10}
                  />
                </div>
              </div>
            </div>

            {/* Principal Officer Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <UserIcon className="h-4 w-4" />
                Principal Officer Information
              </div>

              <AlertContainer>
                <ShieldIcon className="h-4 w-4" />
                <AlertDescription>
                  SSN and Date of Birth are optional for updates. Leave blank to
                  keep existing values.
                </AlertDescription>
              </AlertContainer>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-principalFirstName">First Name *</Label>
                  <Input
                    id="edit-principalFirstName"
                    placeholder="First name"
                    value={editBusinessForm.principalOfficer.firstName}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          firstName: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-principalLastName">Last Name *</Label>
                  <Input
                    id="edit-principalLastName"
                    placeholder="Last name"
                    value={editBusinessForm.principalOfficer.lastName}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          lastName: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-ssn">
                  Social Security Number (optional for updates)
                </Label>
                <Input
                  id="edit-ssn"
                  type="password"
                  placeholder="Leave blank to keep existing"
                  value={editBusinessForm.principalOfficer.ssn}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d-]/g, '');
                    if (value.length === 3 && !value.includes('-')) {
                      value = value + '-';
                    } else if (
                      value.length === 6 &&
                      value.split('-').length === 2
                    ) {
                      value = value + '-';
                    }
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      principalOfficer: {
                        ...prev.principalOfficer,
                        ssn: value.slice(0, 11),
                      },
                    }));
                  }}
                  maxLength={11}
                />
              </div>

              <div className="space-y-2">
                <Label>Date of Birth (optional for updates)</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Select
                    value={editBusinessForm.principalOfficer.dobMonth}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          dobMonth: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.principalOfficer.dobDay}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          dobDay: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="DD" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={editBusinessForm.principalOfficer.dobYear}
                    onValueChange={(value) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          dobYear: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="YYYY" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 80 }, (_, i) => {
                        const year = new Date().getFullYear() - 18 - i;
                        return (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-occupation">Occupation *</Label>
                  <Input
                    id="edit-occupation"
                    placeholder="e.g., CEO, Owner"
                    value={editBusinessForm.principalOfficer.occupation}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          occupation: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-citizenshipStatus">
                    Citizenship Status *
                  </Label>
                  <Select
                    value={editBusinessForm.principalOfficer.citizenshipStatus}
                    onValueChange={(value: CitizenshipStatus) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          citizenshipStatus: value,
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIZENSHIP_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-principalAddress">
                  Principal Officer Address *
                </Label>
                <Input
                  id="edit-principalAddress"
                  placeholder="Street address"
                  value={editBusinessForm.principalOfficer.address.line1}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      principalOfficer: {
                        ...prev.principalOfficer,
                        address: {
                          ...prev.principalOfficer.address,
                          line1: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <Input
                  placeholder="Apt, suite, etc. (optional)"
                  value={editBusinessForm.principalOfficer.address.line2}
                  onChange={(e) =>
                    setEditBusinessForm((prev) => ({
                      ...prev,
                      principalOfficer: {
                        ...prev.principalOfficer,
                        address: {
                          ...prev.principalOfficer.address,
                          line2: e.target.value,
                        },
                      },
                    }))
                  }
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="City"
                    value={editBusinessForm.principalOfficer.address.city}
                    onChange={(e) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          address: {
                            ...prev.principalOfficer.address,
                            city: e.target.value,
                          },
                        },
                      }))
                    }
                  />
                  <Select
                    value={editBusinessForm.principalOfficer.address.state}
                    onValueChange={(value: USState) =>
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          address: {
                            ...prev.principalOfficer.address,
                            state: value,
                          },
                        },
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="State" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="ZIP"
                    value={editBusinessForm.principalOfficer.address.zip}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^\d-]/g, '')
                        .slice(0, 10);
                      setEditBusinessForm((prev) => ({
                        ...prev,
                        principalOfficer: {
                          ...prev.principalOfficer,
                          address: {
                            ...prev.principalOfficer.address,
                            zip: value,
                          },
                        },
                      }));
                    }}
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditBusinessDialogOpen(false);
                setEditBusinessForm(INITIAL_ONBOARDING_FORM);
              }}
              disabled={updateBusinessInfo.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateBusinessInfo}
              disabled={updateBusinessInfo.isPending}
            >
              {updateBusinessInfo.isPending ? (
                <>
                  <CircleNotchIcon className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
